import { askConfirm, escapeHtml } from '@colplat/ui-web';
import { apiFetch } from '../api.js';
import { appHref } from '../globals.js';

/**
 * Files attached to a transaction, on the client — legacy's upload control,
 * its thumbnails and its HEIC handling.
 *
 * **The browser does the resizing.** That is where legacy's server-side
 * thumbnailing went, for the reasons already recorded for icons: it keeps an
 * image library out of `api`, and it means someone can attach the 6 MB photo
 * their phone just took and get a sensible file rather than an error telling
 * them to go and resize it first. A full-size original is kept only when it
 * is already small; anything larger is downscaled once, here, before it ever
 * leaves the machine.
 *
 * The server still enforces every limit. A rule applied in a browser is a
 * courtesy to the person using it, never a control.
 */

/** Beyond this, an image is downscaled before upload. A receipt does not need more. */
const MAX_IMAGE_EDGE = 1600;
/** Roughly the point where JPEG stops paying for itself on a photographed document. */
const IMAGE_QUALITY = 0.82;
const THUMB_EDGE = 160;
/** The server's own cap, repeated so the refusal happens before a long upload rather than after. */
const MAX_BYTES = 8 * 1024 * 1024;

export interface AttachmentDto {
  id: string;
  transactionId: string;
  originalName: string;
  mime: string;
  size: number;
  thumb: string;
  created: string | null;
  /**
   * Signed, app-relative, and short-lived — the server mints it with the list.
   *
   * Empty when this tile came from the local replica rather than from that
   * request. The replica carries the attachment ROW, thumbnail and all, but
   * never the file: a receipt is megabytes and a ledger has thousands, so
   * filling a device with them to serve a case that rarely arises is the wrong
   * trade — and IndexedDB eviction would make it an unreliable one. See
   * ADR-0025.
   */
  url: string;
}

/**
 * Where a tile links to.
 *
 * The path comes from the server already signed, because a link and an
 * `<img>` cannot send the bearer token every other request here carries — see
 * `signedContentPath` in services/api. All this does is prefix the mount
 * point, which the client learns at runtime and never hardcodes (invariant
 * #10).
 */
export function attachmentHref(file: Pick<AttachmentDto, 'url'>): string {
  return appHref(file.url);
}

/**
 * Open a file in a new tab, for a caller that has to ask the server for the
 * link first — the wallet log's thumbnails, whose rows come from the replica
 * and carry no signed url.
 *
 * The tab opens blank, synchronously, while this is still the click: a tab
 * opened after an `await` is no longer the click's own, and Safari blocks it
 * as a pop-up. The caller sets `location` when the link arrives, and closes
 * it if none does. Tiles elsewhere are plain `target="_blank"` links and
 * need none of this.
 *
 * The opener is cut by hand rather than with `noopener`, which makes
 * `window.open` return null — and the caller needs the handle. The content
 * route is sandboxed (CSP `sandbox`) as well.
 */
export function openAttachmentTab(): Window | null {
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;
  return tab;
}

export function formatBytes(size: number): string {
  if (size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * One file's tile: its thumbnail if it has one, otherwise what it is.
 *
 * A type badge rather than a generic paperclip for everything, because "there
 * is a file here" and "there is a PDF here" answer different questions, and
 * the second one is usually the one being asked.
 */
export function attachmentTileHtml(file: AttachmentDto, opts: { removable?: boolean } = {}): string {
  const label = `${file.originalName}${file.size ? ` · ${formatBytes(file.size)}` : ''}`;
  const face = file.thumb
    ? `<img src="${escapeHtml(file.thumb)}" alt="" loading="lazy">`
    : `<span class="wl-att__kind">${escapeHtml(kindOf(file.mime))}</span>`;
  /*
   * No signed url means the file itself is not reachable from here — the row
   * came out of the local replica, which carries the thumbnail and not the
   * bytes (ADR-0025).
   *
   * It renders as a tile that says so rather than as a link that fails. Not a
   * `disabled` button (invariant #4): a `<span>` keeps the name, the size and
   * the picture readable and selectable, and the title says plainly what is
   * missing instead of leaving somebody to conclude the file has been lost.
   */
  const offlineLabel = `${label} — opening the original needs a connection`;
  const body = file.url
    ? `<a class="wl-att__link" href="${escapeHtml(attachmentHref(file))}" target="_blank" rel="noopener noreferrer"
      title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">${face}</a>`
    : `<span class="wl-att__link wl-att__link--offline" role="img"
      title="${escapeHtml(offlineLabel)}" aria-label="${escapeHtml(offlineLabel)}">${face}</span>`;
  return `<span class="wl-att" data-att="${escapeHtml(file.id)}">
    ${body}
    ${opts.removable
      ? `<button type="button" class="wl-att__x" data-att-remove="${escapeHtml(file.id)}"
          aria-label="Remove ${escapeHtml(file.originalName)}" title="Remove">&times;</button>`
      : ''}
  </span>`;
}

function kindOf(mime: string): string {
  if (mime === 'application/pdf') return 'PDF';
  if (mime.startsWith('text/')) return 'TXT';
  if (mime.startsWith('image/')) return 'IMG';
  return 'FILE';
}

/**
 * Read a file, shrink it if it is a large image, and send it.
 *
 * HEIC is passed through untouched: Safari reads it, `createImageBitmap`
 * generally cannot, and converting it here would mean shipping a decoder.
 * The server accepts it, and a browser that cannot render one simply shows
 * the type badge rather than a preview — which is the honest outcome, not a
 * broken image.
 */
export async function uploadAttachment(
  token: string, transactionId: string, file: File,
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (file.size > MAX_BYTES && !file.type.startsWith('image/')) {
    return { ok: false, message: `${file.name} is larger than ${MAX_BYTES / 1024 / 1024} MB.` };
  }

  let bytes: Blob = file;
  let mime = (file.type || 'application/octet-stream').toLowerCase();
  let thumbDataUrl = '';

  if (mime.startsWith('image/') && mime !== 'image/heic' && mime !== 'image/heif') {
    const bitmap = await createImageBitmap(file).catch(() => undefined);
    if (bitmap) {
      try {
        const shrunk = await downscale(bitmap, MAX_IMAGE_EDGE, IMAGE_QUALITY);
        // Only if it actually helped: re-encoding a small PNG as JPEG can
        // make it bigger, and a lossy round trip for nothing is the worst of
        // both.
        if (shrunk && shrunk.size < file.size) {
          bytes = shrunk;
          mime = 'image/jpeg';
        }
        thumbDataUrl = await thumbnail(bitmap);
      } finally {
        bitmap.close();
      }
    }
  }

  if (bytes.size > MAX_BYTES) {
    return { ok: false, message: `${file.name} is larger than ${MAX_BYTES / 1024 / 1024} MB, even after resizing.` };
  }

  const res = await apiFetch(`/apps/webledger/transactions/${transactionId}/attachments`, {
    method: 'POST', token,
    body: { name: file.name, mime, dataBase64: await toBase64(bytes), thumbDataUrl },
  }).catch(() => undefined);

  if (res?.ok) return { ok: true };
  if (!res) return { ok: false, message: 'Could not reach the server — check your connection.' };
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  return { ok: false, message: body.message ?? `${file.name} could not be attached.` };
}

export async function deleteAttachment(token: string, id: string): Promise<boolean> {
  const res = await apiFetch(`/apps/webledger/attachments/${id}`, { method: 'DELETE', token })
    .catch(() => undefined);
  return Boolean(res?.ok);
}

// ------------------------------------------------------------------ images

async function downscale(bitmap: ImageBitmap, maxEdge: number, quality: number): Promise<Blob | undefined> {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  if (scale === 1) return undefined;   // already small enough; keep the original bytes
  return render(bitmap, Math.round(bitmap.width * scale), Math.round(bitmap.height * scale), quality);
}

async function thumbnail(bitmap: ImageBitmap): Promise<string> {
  const scale = Math.min(1, THUMB_EDGE / Math.max(bitmap.width, bitmap.height));
  const blob = await render(bitmap, Math.round(bitmap.width * scale), Math.round(bitmap.height * scale), 0.7);
  if (!blob) return '';
  return `data:image/jpeg;base64,${await toBase64(blob)}`;
}

async function render(bitmap: ImageBitmap, width: number, height: number, quality: number): Promise<Blob | undefined> {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);
  const context = canvas.getContext('2d');
  if (!context) return undefined;
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob | undefined>((resolve) => {
    canvas.toBlob((blob) => resolve(blob ?? undefined), 'image/jpeg', quality);
  });
}

/**
 * Base64 without a `FileReader` data-URL round trip on the main thread.
 *
 * Chunked, because `String.fromCharCode(...bytes)` on a multi-megabyte array
 * exceeds the argument limit and throws — on exactly the large files this
 * path exists for.
 */
async function toBase64(blob: Blob): Promise<string> {
  const buffer = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < buffer.length; i += CHUNK) {
    binary += String.fromCharCode(...buffer.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * The markup for a files bar, given an id prefix.
 *
 * Emitted rather than written twice, because the full editor and the short
 * form now both have one and a bar that drifted into two shapes would be two
 * sets of styling bugs. The prefix is what keeps the ids unique on a page
 * that has both open.
 */
/**
 * Whether a paste is somebody attaching a screenshot.
 *
 * Pasting is how a screenshot gets anywhere — it is on the clipboard and
 * nowhere else, and asking somebody to save it to disk first so they can
 * pick it from a file dialog is three steps to move something they already
 * have.
 *
 * **`types` does not name the image.** A clipboard carrying a file reports
 * `["Files"]`, not `["image/png"]`, and a first version of this looked for
 * the MIME type and therefore never fired once. The type is on the *items*,
 * which is why the caller checks those too before acting.
 *
 * The rule has to leave ordinary pasting alone, and what makes that possible
 * is that copying a cell from a spreadsheet, or a line from a page, puts
 * text on the clipboard beside its picture. So into a field somebody types
 * in, text wins and nothing is intercepted. Anywhere else there is nothing
 * for text to go into, and the image is plainly the point.
 *
 * Decided from `types` alone because it has to be decided before
 * `preventDefault`, and reading a file is asynchronous.
 */
export function pasteIsAttachment(types: readonly string[], intoTextField: boolean): boolean {
  const hasFile = types.includes('Files') || types.some((t) => t.startsWith('image/'));
  if (!hasFile) return false;
  if (!intoTextField) return true;
  return !types.some((t) => t === 'text/plain' || t === 'text/html' || t === 'text/rtf');
}

/** Whether what has focus is somewhere a paste of text would land. */
export function isTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  return el.closest('input:not([type=file]),textarea,[contenteditable=""],[contenteditable=true]') !== null;
}

/**
 * A name for something that never had one.
 *
 * The clipboard's own file is called `image.png` every time, so a
 * transaction with three screenshots on it had three attachments with one
 * name between them. The timestamp is local and to the second, which is as
 * precise as "the one I took just now" needs.
 */
export function pastedFileName(mime: string, at: Date): string {
  const two = (n: number): string => String(n).padStart(2, '0');
  const stamp = `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}`
    + `-${two(at.getHours())}${two(at.getMinutes())}${two(at.getSeconds())}`;
  const ext = /^image\/([a-z0-9]+)/.exec(mime)?.[1]?.replace('jpeg', 'jpg') ?? 'png';
  return `pasted-${stamp}.${ext}`;
}

export function attachmentBarHtml(prefix: string): string {
  return `
    <!-- On a new entry the files are held here and sent when it is saved
         (holdUntilSaved); hidden only where neither can happen. -->
    <div class="wl-att-bar mt-3" id="${prefix}Files" hidden>
      <span class="cf-h">Files</span>
      <span id="${prefix}FileList"></span>
      <label class="btn btn-sm btn-outline-secondary mb-0" id="${prefix}FileAddLabel">
        Attach…
        <input type="file" id="${prefix}FileInput" multiple hidden
          accept="image/*,application/pdf,text/plain,text/csv">
      </label>
      <span class="text-secondary small" id="${prefix}FileNote" role="status"></span>
    </div>`;
}

export interface AttachmentBarOptions {
  /** The transaction to attach to, or undefined while there is not one yet. */
  txId: () => string | undefined;
  token: string;
  /** False on a form that is only being watched — the tiles lose their remove buttons. */
  canEdit: () => boolean;
  /**
   * This transaction's attachment rows from the local replica, thumbnails and
   * all — what the bar shows before, and instead of, the signed urls.
   */
  localFiles?: (transactionId: string) => AttachmentDto[];
  /**
   * True on a NEW entry the form will create: files chosen or pasted are
   * held here, shown with a preview, and sent by `sendHeld` once Save has
   * created the entry. Asked from use — "only after saving and reopening
   * it works" — and sent right after the save rather than inside it, so
   * the save request stays small and a file that will not go never costs
   * the entry.
   */
  holdUntilSaved?: () => boolean;
  notify: (message: string) => void;
}

/** A file waiting for its entry to be saved. */
interface HeldFile { key: string; file: File; preview: string }

export interface MountedAttachmentBar {
  /** Re-read the list from the server. Call after the transaction changes. */
  reload: () => Promise<void>;
  /** Repaint from what is already held — for a read-only flip, say. */
  render: () => void;
  /** How many files are waiting for the entry to be saved. */
  heldCount: () => number;
  /**
   * Send the held files to the entry Save just created, one by one. Each
   * one that goes leaves the list; the first that fails stops it, and its
   * reason comes back (empty when every file went). The rest are dropped
   * either way — the entry now exists, and its own bar is where to try again.
   */
  sendHeld: (transactionId: string) => Promise<string>;
  /** Forget the held files — the form was cancelled or reset. */
  dropHeld: () => void;
  dispose: () => void;
}

/** A held file's tile: the same face as a saved one, saying it is not saved yet. */
function heldTileHtml(held: HeldFile): string {
  const label = `${held.file.name} · ${formatBytes(held.file.size)} — attached when you save`;
  const face = held.preview
    ? `<img src="${escapeHtml(held.preview)}" alt="">`
    : `<span class="wl-att__kind">${escapeHtml(kindOf(held.file.type))}</span>`;
  return `<span class="wl-att wl-att--held" data-att="${escapeHtml(held.key)}">
    <span class="wl-att__link" role="img" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">${face}</span>
    <button type="button" class="wl-att__x" data-att-remove="${escapeHtml(held.key)}"
      aria-label="Remove ${escapeHtml(held.file.name)}" title="Remove">&times;</button>
  </span>`;
}

/**
 * Wire a files bar: list, attach, remove.
 *
 * Lives here rather than in either form because none of it is particular to
 * one. Uploading several files one at a time with a running count is the
 * only part that looks like a decision, and it is: they can be megabytes
 * each, and a silent pause is indistinguishable from a dead button.
 */
export function mountAttachmentBar(
  root: ParentNode, prefix: string, opts: AttachmentBarOptions,
): MountedAttachmentBar {
  const bar = root.querySelector<HTMLElement>(`#${prefix}Files`)!;
  const list = root.querySelector<HTMLElement>(`#${prefix}FileList`)!;
  const addLabel = root.querySelector<HTMLElement>(`#${prefix}FileAddLabel`)!;
  const input = root.querySelector<HTMLInputElement>(`#${prefix}FileInput`)!;
  const note = root.querySelector<HTMLElement>(`#${prefix}FileNote`)!;
  let files: AttachmentDto[] = [];
  let held: HeldFile[] = [];
  let heldSeq = 0;
  const holding = (): boolean => opts.txId() === undefined && (opts.holdUntilSaved?.() ?? false);

  function render(): void {
    const id = opts.txId();
    if (id === undefined && holding()) {
      bar.hidden = false;
      addLabel.hidden = false;
      list.innerHTML = held.length === 0
        ? '<span class="text-secondary small">None yet. A screenshot can be pasted straight in.</span>'
        : held.map(heldTileHtml).join('');
      return;
    }
    bar.hidden = id === undefined;
    if (id === undefined) return;
    addLabel.hidden = !opts.canEdit();
    /*
     * The empty state is the only place pasting is mentioned, and it is
     * enough: it is on screen exactly when somebody is about to attach their
     * first file to this transaction, and gone the moment they have. A
     * standing hint beside the button would be there for ever, on every
     * entry, to say a thing worth reading once.
     */
    list.innerHTML = files.length === 0
      ? `<span class="text-secondary small">None yet.${
        opts.canEdit() ? ' A screenshot can be pasted straight in.' : ''}</span>`
      : files.map((f) => attachmentTileHtml(f, { removable: opts.canEdit() })).join('');
  }

  /*
   * The replica first, then the signed urls if they can be had.
   *
   * The rows are already on the device, so the tiles — name, size, thumbnail —
   * draw immediately and draw offline. The request that follows adds nothing
   * but the short-lived signed links, which is the only part of an attachment
   * that has to come from the server, so failing it costs the ability to OPEN
   * a file and not the ability to see that one is there.
   */
  async function reload(): Promise<void> {
    const id = opts.txId();
    if (id === undefined) { files = []; render(); return; }
    files = opts.localFiles?.(id) ?? [];
    render();

    const res = await apiFetch(`/apps/webledger/transactions/${id}/attachments`, { token: opts.token })
      .catch(() => undefined);
    if (!res?.ok) return;
    files = ((await res.json()) as { attachments: AttachmentDto[] }).attachments;
    render();
  }

  /** Hold files for an entry that does not exist yet, with a preview for images. */
  function hold(chosen: readonly File[]): void {
    for (const file of chosen) {
      heldSeq += 1;
      held.push({
        key: `held-${heldSeq}`, file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      });
    }
    note.textContent = '';
    note.classList.remove('text-danger');
    render();
  }

  function dropHeld(): void {
    for (const h of held) if (h.preview) URL.revokeObjectURL(h.preview);
    held = [];
    render();
  }

  async function sendHeld(transactionId: string): Promise<string> {
    const queue = held;
    held = [];
    let failed = '';
    for (const [index, h] of queue.entries()) {
      note.textContent = queue.length > 1 ? `Attaching ${index + 1} of ${queue.length}…` : `Attaching ${h.file.name}…`;
      const result = failed ? undefined : await uploadAttachment(opts.token, transactionId, h.file);
      if (result && !result.ok) failed = result.message;
      if (h.preview) URL.revokeObjectURL(h.preview);
    }
    note.textContent = '';
    return failed;
  }

  /** The one upload loop, whether the files came from a dialog or a clipboard. */
  async function attach(chosen: readonly File[]): Promise<void> {
    const id = opts.txId();
    if (chosen.length > 0 && id === undefined && holding()) { hold(chosen); return; }
    if (chosen.length === 0 || id === undefined) return;
    addLabel.classList.add('disabled');
    let failed = '';
    for (const [index, file] of chosen.entries()) {
      note.textContent = chosen.length > 1
        ? `Attaching ${index + 1} of ${chosen.length}…`
        : `Attaching ${file.name}…`;
      const result = await uploadAttachment(opts.token, id, file);
      if (!result.ok) { failed = result.message; break; }
    }
    addLabel.classList.remove('disabled');
    note.textContent = failed;
    note.classList.toggle('text-danger', failed !== '');
    if (failed) opts.notify(failed);
    await reload();
  }

  const onChange = (): void => {
    const chosen = [...(input.files ?? [])];
    input.value = '';   // so choosing the same file twice in a row still fires
    void attach(chosen);
  };

  /**
   * A screenshot, pasted.
   *
   * Bound to the form this bar lives in rather than to the bar itself: the
   * bar is never what has focus when somebody presses the paste key, so a
   * listener on it would never fire. It is not bound to the document either
   * — two editors can be mounted at once (the short form hands over to the
   * full one) and the paste belongs to the one being typed into.
   *
   * `pasteIsAttachment` decides before anything is read, so a paste that
   * might be text is left entirely alone — no `preventDefault`, nothing.
   */
  const onPaste = (event: ClipboardEvent): void => {
    const data = event.clipboardData;
    if (!data || (opts.txId() === undefined ? !holding() : !opts.canEdit())) return;
    if (!pasteIsAttachment([...data.types], isTextEntry(event.target))) return;
    const images = [...data.items]
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null)
      // The clipboard calls every one of them `image.png`.
      .map((file) => new File([file], pastedFileName(file.type, new Date()), { type: file.type }));
    if (images.length === 0) return;
    event.preventDefault();
    void attach(images);
  };

  const onClick = (event: Event): void => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-att-remove]');
    if (!button) return;
    const id = button.dataset['attRemove']!;
    // A held file is not anywhere yet: removing it needs no confirmation.
    const waiting = held.find((h) => h.key === id);
    if (waiting) {
      event.preventDefault();
      if (waiting.preview) URL.revokeObjectURL(waiting.preview);
      held = held.filter((h) => h !== waiting);
      render();
      return;
    }
    if (!opts.canEdit()) return;
    event.preventDefault();
    const file = files.find((f) => f.id === id);
    if (!file) return;
    void (async () => {
      const go = await askConfirm({
        title: `Remove ${file.originalName}?`,
        message: 'Deletion is soft: it stays under "Deleted records", where anyone who can '
          + 'delete it can put it back.',
        confirm: 'Remove it', cancel: 'Keep it', tone: 'danger',
      });
      if (!go) return;
      if (!await deleteAttachment(opts.token, id)) {
        // Into the bar's own note as well as the announcement — an upload
        // failure has always landed there, and this one went to `notify`
        // alone, which is the `.cf-sr` region and therefore nowhere a
        // sighted user looks. Same bar, same failure, same place.
        const message = 'Could not remove that file.';
        note.textContent = message;
        note.classList.add('text-danger');
        opts.notify(message);
        return;
      }
      note.textContent = '';
      note.classList.remove('text-danger');
      await reload();
    })();
  };

  input.addEventListener('change', onChange);
  list.addEventListener('click', onClick);
  // The form around the bar, or the bar itself if it has none to belong to.
  const pasteHost: HTMLElement = bar.closest('form') ?? bar;
  pasteHost.addEventListener('paste', onPaste);
  void reload();

  return {
    reload, render, heldCount: () => held.length, sendHeld, dropHeld,
    dispose: () => {
      dropHeld();
      input.removeEventListener('change', onChange);
      list.removeEventListener('click', onClick);
      pasteHost.removeEventListener('paste', onPaste);
    },
  };
}
